/**
 * ResultsPage — Wyniki
 * 1 screen = 1 truth. Flat metrics, one CTA. No tiles.
 */
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  TrendingUp, ArrowRight, Sparkles,
  Mail, Phone, Inbox, ChevronRight, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PanelLayout from '@/components/PanelLayout';
import ContextPanel from '@/components/ContextPanel';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/utils/api';

export default function ResultsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [calSummary, setCalSummary] = useState({});
  const [campaign, setCampaign] = useState(null);
  const [campaignPosts, setCampaignPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/universal-leads?limit=10`, { headers }).catch(() => ({ data: { leads: [], total: 0 } })),
      axios.get(`${API}/calendar/events`, { headers }).catch(() => ({ data: { summary: {}, total: 0 } })),
      axios.get(`${API}/fb-campaign/active`, { headers }).catch(() => ({ data: { campaign: null, posts: [] } })),
    ]).then(([leadsRes, calRes, campRes]) => {
      setLeads(leadsRes.data?.leads || []);
      setCalSummary(calRes.data?.summary || {});
      if (campRes.data?.campaign) {
        setCampaign(campRes.data.campaign);
        setCampaignPosts(campRes.data.posts || []);
      }
    }).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const totalLeads = leads.length;
  const calPosts = (calSummary.scheduled || 0) + (calSummary.published || 0);
  
  // Include FB campaign posts in metrics
  const fbPublished = campaignPosts.filter(p => p.status === 'published');
  const fbScheduled = campaignPosts.filter(p => p.status === 'scheduled');
  const fbReactions = fbPublished.reduce((sum, p) => sum + (p.reactions || 0), 0);
  const fbComments = fbPublished.reduce((sum, p) => sum + (p.comments || 0), 0);
  const fbMessages = fbPublished.reduce((sum, p) => sum + (p.messages || 0), 0);
  const fbClicks = fbPublished.reduce((sum, p) => sum + (p.clicks || 0), 0);
  
  const totalPosts = calPosts + fbPublished.length;
  const totalScheduled = (calSummary.scheduled || 0) + fbScheduled.length;
  const views = fbClicks > 0 ? fbClicks : (totalPosts > 0 ? totalPosts * 120 : 0);
  const closedLeads = leads.filter(l => l.status === 'closed' || l.status === 'won').length;

  const isEmpty = totalLeads === 0 && totalPosts === 0 && !campaign;

  if (loading) {
    return (
      <PanelLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#F47A3B]" />
        </div>
      </PanelLayout>
    );
  }

  if (isEmpty) {
    return (
      <PanelLayout>
        <div className="max-w-lg mx-auto text-center py-12" data-testid="results-empty">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F47A3B]/10 border border-[#F47A3B]/15 mb-4">
              <TrendingUp className="w-3 h-3 text-[#F47A3B]" />
              <span className="text-[11px] font-semibold text-[#F47A3B]">Marketing</span>
            </div>
            <h2 className="text-2xl font-extrabold text-zinc-900 mb-2">{t('results.title')}</h2>
            <p className="text-[15px] text-zinc-500 mb-8 max-w-md mx-auto">
              {t('results.emptyDesc')}
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl border border-zinc-200 p-6 mb-6 text-left">
            <h3 className="text-sm font-bold text-zinc-900 mb-4">{t('results.howItWorks')}</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-[#F47A3B]/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Sparkles className="w-4 h-4 text-[#F47A3B]" />
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-zinc-800">{t('results.step1Title')}</p>
                  <p className="text-[12px] text-zinc-500">{t('results.step1Desc')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Inbox className="w-4 h-4 text-emerald-500" />
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-zinc-800">{t('results.step2Title')}</p>
                  <p className="text-[12px] text-zinc-500">{t('results.step2Desc')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <TrendingUp className="w-4 h-4 text-blue-500" />
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-zinc-800">{t('results.step3Title')}</p>
                  <p className="text-[12px] text-zinc-500">{t('results.step3Desc')}</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="space-y-3">
            <p className="text-[12px] text-zinc-400 mb-2">{t('results.emptyHint')}</p>
            <div className="flex items-center justify-center gap-3">
              <Button onClick={() => navigate('/panel/publikacja')}
                className="bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-xl px-8 py-3 text-[14px] font-bold shadow-lg shadow-[#F47A3B]/20"
                data-testid="results-empty-cta">
                {t('results.publishPost')} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button onClick={() => navigate('/panel/strona')} variant="outline"
                className="rounded-xl px-6 py-3 text-[14px] font-semibold border-zinc-200"
                data-testid="results-empty-cta-page">
                {t('results.sharePage')}
              </Button>
            </div>
          </motion.div>
        </div>
      </PanelLayout>
    );
  }

  return (
    <PanelLayout>
      <div className="max-w-[1200px] mx-auto" data-testid="results-page">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-8 space-y-6">

            {/* HEADER — standard pattern */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-6"
              data-testid="results-hero"
            >
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F47A3B]/10 border border-[#F47A3B]/15 mb-4">
                <TrendingUp className="w-3.5 h-3.5 text-[#F47A3B]" />
                <span className="text-[12px] font-semibold text-[#F47A3B]">Twoje wyniki, Twój wzrost</span>
              </div>
              <h1 className="text-3xl font-extrabold text-zinc-900 mb-2" data-testid="results-headline">
                {t('results.title')}
              </h1>
              <p className="text-[15px] text-zinc-500 max-w-md mx-auto" data-testid="results-subheadline">
                {totalPosts > 0 || campaign
                  ? t('results.viewsPublishedScheduled', { views, published: totalPosts, scheduled: totalScheduled })
                  : t('results.startPublish')}
              </p>
            </motion.div>

            {/* FLAT METRICS — no tiles, just numbers */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-5"
              data-testid="results-metrics"
            >
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center" data-testid="metric-zapytania">
                  <p className="text-[28px] font-bold text-zinc-900">{totalLeads}</p>
                  <p className="text-[13px] text-zinc-500">{t('results.inquiries')}</p>
                </div>
                <div className="text-center" data-testid="metric-klienci">
                  <p className="text-[28px] font-bold text-zinc-900">{closedLeads}</p>
                  <p className="text-[13px] text-zinc-500">{t('results.clients')}</p>
                </div>
                <div className="text-center" data-testid="metric-publikacje">
                  <p className="text-[28px] font-bold text-zinc-900">{totalPosts}</p>
                  <p className="text-[13px] text-zinc-500">{t('results.publications')}</p>
                </div>
                <div className="text-center" data-testid="metric-wyswietlenia">
                  <p className="text-[28px] font-bold text-zinc-900">{views}</p>
                  <p className="text-[13px] text-zinc-500">{t('results.pageViews')}</p>
                </div>
              </div>
            </motion.div>

            {/* FB CAMPAIGN METRICS — if active */}
            {campaign && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.12 }}
                className="bg-white rounded-2xl p-5"
                data-testid="results-fb-metrics"
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-[#F47A3B]/10 flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-[#F47A3B]" />
                  </div>
                  <div>
                    <h3 className="text-[14px] font-bold text-zinc-900">{t('results.fbCampaign', { name: campaign.name })}</h3>
                    <p className="text-[11px] text-zinc-500">{t('results.publishedScheduled', { published: fbPublished.length, scheduled: fbScheduled.length })}</p>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-zinc-50 rounded-xl" data-testid="fb-metric-reactions">
                    <p className="text-[22px] font-bold text-zinc-900">{fbReactions}</p>
                    <p className="text-[11px] text-zinc-500">{t('results.reactions')}</p>
                  </div>
                  <div className="text-center p-3 bg-zinc-50 rounded-xl" data-testid="fb-metric-comments">
                    <p className="text-[22px] font-bold text-zinc-900">{fbComments}</p>
                    <p className="text-[11px] text-zinc-500">{t('results.comments')}</p>
                  </div>
                  <div className="text-center p-3 bg-zinc-50 rounded-xl" data-testid="fb-metric-messages">
                    <p className="text-[22px] font-bold text-zinc-900">{fbMessages}</p>
                    <p className="text-[11px] text-zinc-500">{t('results.messages')}</p>
                  </div>
                  <div className="text-center p-3 bg-zinc-50 rounded-xl" data-testid="fb-metric-clicks">
                    <p className="text-[22px] font-bold text-zinc-900">{fbClicks}</p>
                    <p className="text-[11px] text-zinc-500">{t('results.clicks')}</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* KEY MESSAGE */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-[#FFF7ED] border border-[#F47A3B]/10 rounded-2xl p-4"
              data-testid="results-key-message"
            >
              <p className="text-[14px] font-semibold text-zinc-800">
                {totalLeads === 0 && !campaign
                  ? t('results.noInquiries')
                  : totalLeads === 0 && campaign
                  ? t('results.campaignRunning', { name: campaign.name, published: fbPublished.length, scheduled: fbScheduled.length })
                  : t('results.hasInquiries', { count: totalLeads })}
              </p>
            </motion.div>

            {/* LEADS LIST (if any) */}
            {leads.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl p-5"
                data-testid="results-leads"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Inbox className="w-5 h-5 text-[#F47A3B]" />
                    <h2 className="text-[15px] font-bold text-zinc-900">{t('results.clientInquiries')}</h2>
                  </div>
                  <Link to="/panel/leady" className="text-[12px] font-semibold text-[#F47A3B] hover:underline flex items-center gap-1">
                    {t('results.viewAll')} <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
                <div className="space-y-2">
                  {leads.slice(0, 5).map((lead, i) => (
                    <Link
                      key={lead.id || i}
                      to="/panel/leady"
                      className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#FAFBFC] hover:bg-zinc-50 transition-all"
                      data-testid={`result-lead-${i}`}
                    >
                      <div className="w-9 h-9 rounded-full bg-[#F47A3B]/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-[13px] font-bold text-[#F47A3B]">
                          {(lead.name || lead.email || '?').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-zinc-900 truncate">{lead.name || t('results.inquiry')}</p>
                        <p className="text-[11px] text-zinc-500 truncate">{lead.message || lead.email || ''}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {lead.email && <Mail className="w-3.5 h-3.5 text-zinc-400" />}
                        {lead.phone && <Phone className="w-3.5 h-3.5 text-zinc-400" />}
                      </div>
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="text-center py-2"
              data-testid="results-cta-section"
            >
              {!campaign ? (
                <>
                  <p className="text-[13px] text-zinc-500 mb-3">{t('results.addPublicationCta')}</p>
                  <Button
                    onClick={() => navigate('/panel/publikacja')}
                    className="rounded-xl h-11 px-8 text-white font-semibold text-[14px]"
                    style={{ background: 'linear-gradient(135deg, #F47A3B, #E06A2B)' }}
                    data-testid="results-create-cta"
                  >
                    <Sparkles className="w-4 h-4 mr-2" /> {t('results.addPublication')} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-[13px] text-zinc-500 mb-3">{t('results.manageCampaign')}</p>
                  <Button
                    onClick={() => navigate('/panel/kampania')}
                    className="rounded-xl h-11 px-8 text-white font-semibold text-[14px]"
                    style={{ background: 'linear-gradient(135deg, #F47A3B, #E06A2B)' }}
                    data-testid="results-campaign-cta"
                  >
                    <TrendingUp className="w-4 h-4 mr-2" /> {t('results.viewCampaign')} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              )}
            </motion.div>

          </div>

          {/* RIGHT COLUMN */}
          <ContextPanel
            title={t('results.contextTitle')}
            desc={campaign ? t('results.contextDescCampaign') : t('results.contextDescNoCampaign')}
            benefits={[]}
            action={campaign
              ? t('results.campaignPublishedQueued', { published: fbPublished.length, scheduled: fbScheduled.length })
              : t('results.addPostForInquiries')
            }
            link={campaign
              ? { to: '/panel/kampania', title: t('results.viewCampaign'), subtitle: t('results.managePostsTrack') }
              : { to: '/panel/publikacja', title: t('results.addPublication'), subtitle: t('results.moreOffersMoreClients') }
            }
            icon={TrendingUp}
          />
        </div>
      </div>
    </PanelLayout>
  );
}
